import React from "react";
import ResourceSelectLazyLoading from "../../../components/ResourceSelectLazyLoading";
import DateRangePicker from "../../../components/DateRangePicker";
import moment from "../../../moment";
import { contractTypeOptions, qualificationStatusOptions } from "./config";

/**
 * Renders the external filter bar for the Qualifications table.
 *
 * Receives all filter state and option lists from the parent `Qualifications`
 * component so that it remains a pure presentational component with no
 * direct access to Redux or the TableService.
 */
const QualificationsFilters = ({
  shouldReset,
  contractFilter,
  statusFilter,
  branchFilter,
  departmentFilter,
  areaFilter,
  jobTitleFilter,
  employeeStatusFilter,
  dateFilter,
  dateRange,
  branchesOptions,
  departmentsState,
  areasState,
  jobTitleState,
  employeeStatusState,
  apiDepartmentsOptions,
  apiAreasOptions,
  apiJobTitleOptions,
  apiEmployeeStatusOptions,
  localStorageTableFilters,
  onFilterChange,
  onDateChange,
  t,
  prepopulateFilter
}) => (
  <div className="flex qualification-external-filters">
    <ResourceSelectLazyLoading
      shouldReset={shouldReset}
      showArrow
      showSearch
      allowClear
      className="mr-10 mb-10"
      initialValue={contractFilter}
      value={contractFilter?.value}
      placeholder={t("trg.qualifications.filter_by_contract")}
      staticOptions={contractTypeOptions}
      onChange={(value) => onFilterChange("validContract", value)}
    />
    <ResourceSelectLazyLoading
      shouldReset={shouldReset}
      showArrow
      showSearch
      allowClear
      className="mr-10 mb-10"
      initialValue={statusFilter}
      value={statusFilter?.value}
      placeholder={t("trg.qualifications.filter_by_qualification_status")}
      staticOptions={qualificationStatusOptions}
      onChange={(value) => onFilterChange("qualificationStatus", value)}
    />
    <ResourceSelectLazyLoading
      shouldReset={shouldReset}
      overwritePreviousOptions
      className="mr-10 mb-15"
      showArrow
      value={branchFilter}
      staticOptions={branchesOptions}
      placeholder={t("org.org_chart.filter_by_branch")}
      onChange={(value) => onFilterChange("branchId", value)}
    />
    {!departmentsState.loading && (
      <ResourceSelectLazyLoading
        shouldReset={shouldReset}
        showArrow
        showSearch
        allowClear
        className="mr-10 mb-15"
        placeholder={t("trg.reporting.filter_by_department")}
        apiRoute="departments?order_by_column%5B%5D=name&order_by_type%5B%5D=asc&with%5B%5D=branch"
        searchColumns={["name"]}
        transformer={(values) =>
          values?.map((item) => {
            const text = item.branch.city ? `${item.name} (${item.branch.city})` : item.name;
            return { ...item, text };
          }) || []
        }
        initialValue={prepopulateFilter(
          localStorageTableFilters.anotherFilters?.find((filter) => filter.nameKey === "departmentId")?.value,
          [],
          apiDepartmentsOptions
        )}
        value={departmentFilter?.value}
        onChange={(value) => onFilterChange("departmentId", value)}
      />
    )}
    {!areasState.loading && (
      <ResourceSelectLazyLoading
        shouldReset={shouldReset}
        showArrow
        showSearch
        allowClear
        className="mr-10 mb-15"
        placeholder={t("trg.reporting.filter_by_area")}
        apiRoute="trg/areas?order_by_column%5B%5D=name&order_by_type%5B%5D=asc"
        searchColumns={["name"]}
        value={areaFilter}
        initialValue={prepopulateFilter(
          localStorageTableFilters.anotherFilters?.find((filter) => filter.nameKey === "areaId")?.value,
          [],
          apiAreasOptions
        )}
        onChange={(value) => onFilterChange("areaId", value)}
      />
    )}
    {!jobTitleState.loading && (
      <ResourceSelectLazyLoading
        shouldReset={shouldReset}
        showArrow
        showSearch
        allowClear
        maxTagCount="responsive"
        mode="multiple"
        className="mr-10 mb-15"
        placeholder={t("trg.reporting.filter_by_job_title")}
        apiRoute="job-titles?field%5B%5D=active&op%5B%5D=eq&term%5B%5D=1&order_by_column%5B%5D=name&order_by_type%5B%5D=asc"
        searchColumns={["name"]}
        initialValue={prepopulateFilter(
          localStorageTableFilters.anotherFilters?.find((filter) => filter.nameKey === "jobTitleIds")?.value,
          [],
          apiJobTitleOptions
        )}
        value={jobTitleFilter?.value}
        onChange={(value) => onFilterChange("jobTitleIds", value)}
      />
    )}
    {!employeeStatusState.loading && (
      <ResourceSelectLazyLoading
        shouldReset={shouldReset}
        showArrow
        showSearch
        allowClear
        className="mr-10 mb-10"
        mode="multiple"
        initialValue={prepopulateFilter(
          localStorageTableFilters.anotherFilters?.find((filter) => filter.nameKey === "employeeStatusIds")?.value,
          [
            { value: "all_employee_status", text: t("trg.qualifications.all_employee_status") },
            { value: "no_employee_status", text: t("trg.qualifications.no_employee_status") }
          ],
          apiEmployeeStatusOptions
        )}
        value={employeeStatusFilter?.value}
        placeholder={t("trg.qualifications.filter_by_employee_status")}
        apiRoute="employee-status"
        maxTagCount="responsive"
        staticOptions={[
          { value: "all_employee_status", text: t("trg.qualifications.all_employee_status") },
          { value: "no_employee_status", text: t("trg.qualifications.no_employee_status") }
        ]}
        onChange={(value) => onFilterChange("employeeStatusIds", value)}
      />
    )}
    <DateRangePicker
      size="medium"
      className="mb-10 mr-10"
      style={{ width: 250, minWidth: 250, maxHeight: 40 }}
      value={dateRange}
      defaultValue={
        dateFilter?.value?.start_value && dateFilter?.value?.end_value
          ? [moment(dateFilter.value.start_value), moment(dateFilter.value.end_value)]
          : undefined
      }
      onChange={onDateChange}
    />
  </div>
);

export default QualificationsFilters;
